import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  BookOpen,
  FileText,
  Loader2,
  CheckCircle2,
  Info,
  Layers,
  ListOrdered,
  Sparkles,
  Library,
  Search,
  Trash2,
  ImagePlus,
} from "lucide-react";
import { createLesson, updateLesson, getLessonById } from "../../../api/elearning/lessons/lessons";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { getDocumentsByStatusAndLevel } from "../../../api/elearning/documents/documents";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { getCurrentUser } from "../../../utils/auth";
import { AnimatePresence, motion } from "framer-motion";
import { toTitleCaseVietnamese } from "../../../utils/textUtils";
import { startLessonCreateTour } from "./lessonCreateTour";
import {
  LESSON_THUMB,
  prepareLessonThumbnail,
  formatThumbBytes,
  fileToDataUrl,
} from "./lessonThumbnail";

const DROPDOWN_Z = 10060;

function FieldLabel({ children, required, hint }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {children}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {hint ? (
        <span className="text-xs font-medium text-slate-400">{hint}</span>
      ) : null}
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, children, tourId }) {
  return (
    <section
      className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm md:p-6"
      data-tour={tourId}
    >
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <Icon size={20} strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-relaxed text-slate-500">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function SummaryRow({ label, value, dark = false, muted = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={dark ? "text-xs text-white/60" : "text-xs text-slate-400"}>
        {label}
      </span>
      <span
        className={
          dark
            ? `max-w-[62%] text-right text-sm font-semibold ${
                muted ? "text-white/45" : "text-white"
              }`
            : `max-w-[62%] text-right text-sm font-semibold ${
                muted ? "text-slate-400" : "text-slate-700"
              }`
        }
      >
        {value}
      </span>
    </div>
  );
}

function StatusChip({ icon: Icon, label, highlighted }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
        highlighted
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-slate-200 bg-white text-slate-600"
      }`}
    >
      {Icon ? <Icon size={13} strokeWidth={2.2} /> : null}
      {label}
    </span>
  );
}

const CustomSelect = ({
  label,
  required,
  hint,
  value,
  onChange,
  options,
  placeholder,
  loading,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const syncMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const desiredMax = 260;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    const maxListH = Math.min(
      desiredMax,
      openUp ? spaceAbove - 12 : spaceBelow - 12
    );
    const left = Math.max(
      margin,
      Math.min(r.left, window.innerWidth - r.width - margin)
    );
    setMenuRect({
      left,
      width: r.width,
      maxHeight: Math.max(100, maxListH),
      ...(openUp
        ? { bottom: window.innerHeight - r.top + margin }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }
    syncMenuPosition();
    const onScroll = () => syncMenuPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [isOpen, options.length, syncMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (t?.contains(e.target) || m?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const menuPortal =
    isOpen &&
    menuRect &&
    typeof document !== "undefined" &&
    createPortal(
      <motion.div
        ref={menuRef}
        role="listbox"
        initial={{ opacity: 0, y: menuRect.top != null ? -4 : 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1 }}
        className="fixed overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5"
        style={{
          left: menuRect.left,
          width: menuRect.width,
          zIndex: DROPDOWN_Z,
          ...(menuRect.top != null ? { top: menuRect.top } : {}),
          ...(menuRect.bottom != null ? { bottom: menuRect.bottom } : {}),
          maxHeight: menuRect.maxHeight,
        }}
      >
        <div
          className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
          style={{ maxHeight: menuRect.maxHeight }}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-center text-sm text-slate-400">
              Không có dữ liệu
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                  value === opt.value
                    ? "bg-red-50 font-semibold text-red-700"
                    : "font-medium text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {value === opt.value ? (
                  <CheckCircle2 size={15} className="shrink-0 text-red-600" />
                ) : null}
              </button>
            ))
          )}
        </div>
      </motion.div>,
      document.body
    );

  return (
    <div>
      {label ? (
        <FieldLabel required={required} hint={hint}>
          {label}
        </FieldLabel>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`flex h-11 w-full items-center justify-between gap-2 rounded-2xl border bg-white px-4 text-left text-sm transition-colors ${
          isOpen
            ? "border-red-400 ring-2 ring-red-50"
            : "border-slate-200 hover:border-slate-300"
        } ${
          disabled || loading
            ? "cursor-not-allowed bg-slate-50 opacity-70"
            : "cursor-pointer"
        }`}
      >
        <span
          className={`min-w-0 truncate font-medium ${
            !selectedOption ? "text-slate-400" : "text-slate-800"
          }`}
        >
          {loading
            ? "Đang tải…"
            : selectedOption
              ? selectedOption.label
              : placeholder || "Chọn…"}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-400 transition ${
            isOpen ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {menuPortal}
    </div>
  );
};

/** Dropdown multi-select có ô tìm kiếm trong panel (giống combobox). */
const CustomMultiSearchSelect = ({
  label,
  required,
  hint,
  values = [],
  onChange,
  options = [],
  placeholder,
  loading,
  disabled,
  emptyMessage = "Không có dữ liệu",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const selectedSet = useMemo(
    () => new Set((values || []).map((v) => String(v))),
    [values]
  );

  const selectedOptions = useMemo(
    () => options.filter((opt) => selectedSet.has(String(opt.value))),
    [options, selectedSet]
  );

  const filteredOptions = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      String(opt.label || "")
        .toLowerCase()
        .includes(q)
    );
  }, [options, keyword]);

  const syncMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const desiredMax = 320;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxListH = Math.min(
      desiredMax,
      openUp ? spaceAbove - 12 : spaceBelow - 12
    );
    const left = Math.max(
      margin,
      Math.min(r.left, window.innerWidth - r.width - margin)
    );
    setMenuRect({
      left,
      width: r.width,
      maxHeight: Math.max(160, maxListH),
      ...(openUp
        ? { bottom: window.innerHeight - r.top + margin }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }
    syncMenuPosition();
    const onScroll = () => syncMenuPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [isOpen, options.length, filteredOptions.length, syncMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (t?.contains(e.target) || m?.contains(e.target)) return;
      setIsOpen(false);
      setKeyword("");
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  const toggleValue = (val) => {
    const key = String(val);
    const next = selectedSet.has(key)
      ? values.filter((v) => String(v) !== key)
      : [...values, val];
    onChange?.(next);
  };

  const triggerText = loading
    ? "Đang tải…"
    : selectedOptions.length === 0
      ? placeholder || "Chọn…"
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `Đã chọn ${selectedOptions.length} tài liệu`;

  const listMax = menuRect
    ? Math.max(80, menuRect.maxHeight - 52)
    : 200;

  const menuPortal =
    isOpen &&
    menuRect &&
    typeof document !== "undefined" &&
    createPortal(
      <motion.div
        ref={menuRef}
        role="listbox"
        aria-multiselectable="true"
        initial={{ opacity: 0, y: menuRect.top != null ? -4 : 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1 }}
        className="fixed overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5"
        style={{
          left: menuRect.left,
          width: menuRect.width,
          zIndex: DROPDOWN_Z,
          ...(menuRect.top != null ? { top: menuRect.top } : {}),
          ...(menuRect.bottom != null ? { bottom: menuRect.bottom } : {}),
          maxHeight: menuRect.maxHeight,
        }}
      >
        <div className="border-b border-slate-100 p-2">
          <div className="relative">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              ref={searchRef}
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Tìm tài liệu…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-50"
            />
          </div>
        </div>
        <div
          className="custom-scrollbar min-h-0 overflow-y-auto overscroll-contain p-1"
          style={{ maxHeight: listMax }}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-slate-400">
              <Loader2 size={16} className="animate-spin" />
              Đang tải…
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">
              {options.length === 0 ? emptyMessage : "Không khớp từ khóa"}
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const checked = selectedSet.has(String(opt.value));
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggleValue(opt.value)}
                  className={`flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                    checked
                      ? "bg-red-50 font-semibold text-red-700"
                      : "font-medium text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked
                        ? "border-red-600 bg-red-600 text-white"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {checked ? <CheckCircle2 size={10} /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{opt.label}</span>
                    {opt.subLabel ? (
                      <span className="mt-0.5 block text-xs font-medium uppercase tracking-wide text-slate-400">
                        {opt.subLabel}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </motion.div>,
      document.body
    );

  return (
    <div>
      {label ? (
        <FieldLabel required={required} hint={hint}>
          {label}
        </FieldLabel>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => {
          if (disabled || loading) return;
          setIsOpen((o) => {
            const next = !o;
            if (!next) setKeyword("");
            return next;
          });
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`flex min-h-11 w-full items-center justify-between gap-2 rounded-2xl border bg-white px-4 py-2.5 text-left text-sm transition-colors ${
          isOpen
            ? "border-red-400 ring-2 ring-red-50"
            : "border-slate-200 hover:border-slate-300"
        } ${
          disabled || loading
            ? "cursor-not-allowed bg-slate-50 opacity-70"
            : "cursor-pointer"
        }`}
      >
        <span
          className={`min-w-0 truncate font-medium ${
            selectedOptions.length === 0 ? "text-slate-400" : "text-slate-800"
          }`}
        >
          {triggerText}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-400 transition ${
            isOpen ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {menuPortal}
    </div>
  );
};

const LessonCreateModal = ({ isOpen, onClose, onSaved, lessonId = null }) => {
  const isEdit = Boolean(lessonId);
  const [subjectId, setSubjectId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  /** subject | course | course_or_shared */
  const [docScope, setDocScope] = useState("course_or_shared");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [lessonOrder, setLessonOrder] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");
  const [thumbBytes, setThumbBytes] = useState(0);
  const [thumbBusy, setThumbBusy] = useState(false);
  /** true nếu user gỡ thumbnail cũ (edit) */
  const [thumbCleared, setThumbCleared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);

  const clearThumb = () => {
    if (thumbPreview && String(thumbPreview).startsWith("blob:")) {
      URL.revokeObjectURL(thumbPreview);
    }
    setThumbFile(null);
    setThumbPreview("");
    setThumbBytes(0);
    setThumbCleared(true);
  };

  const resetForm = () => {
    setSubjectId("");
    setCourseId("");
    setCourses([]);
    setDocuments([]);
    setSelectedDocIds([]);
    setDocScope("course_or_shared");
    setTitle("");
    setDescription("");
    setLessonOrder("");
    setIsActive(true);
    if (thumbPreview && String(thumbPreview).startsWith("blob:")) {
      URL.revokeObjectURL(thumbPreview);
    }
    setThumbFile(null);
    setThumbPreview("");
    setThumbBytes(0);
    setThumbCleared(false);
    setLoading(false);
    setBootLoading(false);
    setCoursesLoading(false);
    setDocsLoading(false);
    setThumbBusy(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    let cancelled = false;
    (async () => {
      setSubjectsLoading(true);
      try {
        const rows = await getSubjectsLookup({ is_active: "true" });
        if (!cancelled) setSubjects(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Lỗi khi tải môn học:", err);
        if (!cancelled) setSubjects([]);
      } finally {
        if (!cancelled) setSubjectsLoading(false);
      }

      if (lessonId) {
        setBootLoading(true);
        try {
          const data = await getLessonById(lessonId);
          if (cancelled || !data) return;
          setSubjectId(data.subject_id || "");
          setCourseId(data.course_id || "");
          setTitle(data.title || "");
          setDescription(data.description || "");
          const o = data.order;
          setLessonOrder(
            o !== null && o !== undefined && o !== "" ? String(o) : ""
          );
          setIsActive(data.is_active !== false);
          const docs = Array.isArray(data.documents) ? data.documents : [];
          const ids =
            Array.isArray(data.document_ids) && data.document_ids.length
              ? data.document_ids.map(String)
              : docs.map((d) => String(d.id)).filter(Boolean);
          setSelectedDocIds(ids);
          if (docs.length) {
            setDocuments(docs);
          }
          if (data.thumbnail_url) {
            setThumbPreview(data.thumbnail_url);
            setThumbCleared(false);
          }
        } catch (err) {
          showErrorToast(
            err.response?.data?.message ||
              err.message ||
              "Không tải được bài học để sửa."
          );
          onClose?.();
        } finally {
          if (!cancelled) setBootLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lessonId]);

  useEffect(() => {
    if (!isOpen || !subjectId) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    getAllCoursesNoPaging({ subject_id: subjectId, is_active: "true" })
      .then((data) => {
        if (!cancelled) setCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, subjectId]);

  useEffect(() => {
    if (!isOpen) {
      setDocuments([]);
      return;
    }

    const subjectRow = subjects.find((x) => String(x.id) === String(subjectId));
    const subjectCode = subjectRow?.code || "";

    const canFetchSubject = docScope === "subject" && Boolean(subjectCode);
    const canFetchCourse =
      docScope === "course" && Boolean(courseId);
    const canFetchShared =
      docScope === "course_or_shared" &&
      Boolean(courseId) &&
      Boolean(subjectCode);

    if (!canFetchSubject && !canFetchCourse && !canFetchShared) {
      setDocuments([]);
      return;
    }

    let cancelled = false;
    setDocsLoading(true);

    const filters = { status: "true", scope: docScope };
    if (docScope === "subject") {
      filters.subject = subjectCode;
    } else if (docScope === "course") {
      filters.course_id = courseId;
    } else {
      filters.course_id = courseId;
      filters.subject = subjectCode;
    }

    getDocumentsByStatusAndLevel(filters)
      .then((docs) => {
        if (cancelled) return;
        const list = Array.isArray(docs) ? docs : [];
        setDocuments((prev) => {
          const byId = new Map(list.map((d) => [String(d.id), d]));
          (prev || []).forEach((d) => {
            if (d?.id && !byId.has(String(d.id))) byId.set(String(d.id), d);
          });
          return [...byId.values()];
        });
      })
      .catch(() => {
        if (!cancelled) {
          /* giữ prev nếu đang sửa và đã có docs gắn */
        }
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, courseId, subjectId, subjects, docScope]);

  // Khi load edit: giữ tài liệu đang gắn trước khi API filter trả về
  // (đã set trong boot effect qua documents từ lesson)
  const subjectLabel = useMemo(() => {
    const s = subjects.find((x) => String(x.id) === String(subjectId));
    return s?.name || "";
  }, [subjects, subjectId]);

  const subjectCode = useMemo(() => {
    const s = subjects.find((x) => String(x.id) === String(subjectId));
    return s?.code || "";
  }, [subjects, subjectId]);

  const courseLabel = useMemo(() => {
    const c = courses.find((x) => String(x.id) === String(courseId));
    return c?.name || "";
  }, [courses, courseId]);

  const canPickDocuments =
    docScope === "subject"
      ? Boolean(subjectCode)
      : docScope === "course"
        ? Boolean(courseId)
        : Boolean(courseId && subjectCode);

  const docsScopeHint =
    docScope === "subject"
      ? !subjectId
        ? "Chọn môn học để tải tài liệu theo môn."
        : null
      : !courseId
        ? "Chọn khóa học để tải tài liệu theo phạm vi đã chọn."
        : null;

  const selectedDocs = useMemo(
    () =>
      documents.filter((d) =>
        selectedDocIds.includes(String(d.id))
      ),
    [documents, selectedDocIds]
  );

  const documentOptions = useMemo(
    () =>
      documents.map((d) => ({
        value: String(d.id),
        label: d.file_name || "Tài liệu",
        subLabel: [d.type, d.course_name || "Dùng chung môn"]
          .filter(Boolean)
          .join(" · "),
      })),
    [documents]
  );

  const completionCount = [
    Boolean(subjectId),
    Boolean(courseId),
    Boolean(title.trim()),
    selectedDocIds.length > 0,
  ].filter(Boolean).length;

  const toggleDoc = (id) => {
    const key = String(id);
    setSelectedDocIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleThumbPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setThumbBusy(true);
    try {
      const prepared = await prepareLessonThumbnail(file);
      if (thumbPreview && String(thumbPreview).startsWith("blob:")) {
        URL.revokeObjectURL(thumbPreview);
      }
      setThumbFile(prepared.file);
      setThumbPreview(prepared.previewUrl);
      setThumbBytes(prepared.bytes);
      setThumbCleared(false);
    } catch (err) {
      showErrorToast(err.message || "Không xử lý được ảnh thumbnail.");
    } finally {
      setThumbBusy(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!subjectId) return showErrorToast("Vui lòng chọn môn học.");
      if (!courseId) return showErrorToast("Vui lòng chọn khóa học.");
      if (!title.trim()) return showErrorToast("Vui lòng nhập tên bài học.");
      if (selectedDocIds.length === 0) {
        return showErrorToast("Vui lòng chọn ít nhất 1 tài liệu.");
      }

      const payload = {
        title,
        description,
        course_id: courseId,
        document_ids: selectedDocIds,
        created_by: getCurrentUser()?.id || null,
      };
      if (String(lessonOrder).trim() !== "") {
        const o = parseInt(String(lessonOrder).trim(), 10);
        if (Number.isNaN(o) || o < 0) {
          return showErrorToast("Số thứ tự phải là số nguyên ≥ 0.");
        }
        payload.order = o;
      } else if (isEdit) {
        showErrorToast("Vui lòng nhập số thứ tự hiển thị.");
        return;
      }

      if (isEdit) {
        payload.is_active = isActive;
      }

      setLoading(true);

      if (thumbFile) {
        payload.thumbnail_base64 = await fileToDataUrl(thumbFile);
      } else if (isEdit && thumbCleared) {
        payload.thumbnail_base64 = "";
      }

      if (isEdit) {
        await updateLesson(lessonId, payload);
        showSuccessToast("Đã cập nhật bài học.");
      } else {
        await createLesson(payload);
        showSuccessToast("Tạo bài học thành công!");
      }
      onSaved?.();
      onClose();
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          error.message ||
          (isEdit
            ? "Có lỗi xảy ra khi cập nhật bài học."
            : "Có lỗi xảy ra khi tạo bài học.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    if (
      title ||
      subjectId ||
      courseId ||
      lessonOrder ||
      selectedDocIds.length ||
      thumbFile
    ) {
      showConfirmDialog({
        title: "Xác nhận huỷ",
        text: "Dữ liệu đang nhập sẽ bị mất, bạn có chắc chắn?",
      }).then((res) => res.isConfirmed && onClose());
      return;
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-slate-900/55 backdrop-blur-md"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
          >
            <div
              className="pointer-events-auto flex max-h-[min(92vh,860px)] w-full max-w-5xl flex-col overflow-hidden rounded-[1.75rem] border border-slate-100 bg-white shadow-2xl"
              data-tour="lesson-create-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="shrink-0 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white px-6 pb-5 pt-6 md:px-8"
                data-tour="lesson-create-header"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg shadow-red-200">
                      <BookOpen size={20} />
                    </span>
                    <div className="min-w-0">
                      <h2 className="text-xl font-black tracking-tight text-slate-800">
                        {isEdit ? "Sửa bài học" : "Thêm bài học mới"}
                      </h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {isEdit
                          ? "Chỉnh sửa theo cùng bố cục form thêm mới — cột phải xem trước realtime."
                          : "Nhập cấu hình bên trái — cột phải cập nhật kết quả khởi tạo theo thời gian thực."}
                      </p>
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-1"
                    data-tour="lesson-create-guide-btn"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        window.setTimeout(() => startLessonCreateTour(), 80)
                      }
                      title="Hướng dẫn nhanh"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <Sparkles size={16} strokeWidth={2.2} />
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <StatusChip
                    icon={BookOpen}
                    label={subjectLabel || "Chưa chọn môn"}
                  />
                  <StatusChip
                    icon={Layers}
                    label={courseLabel || "Chưa chọn khóa"}
                  />
                  <StatusChip
                    icon={CheckCircle2}
                    label={`Hoàn thiện ${completionCount}/4 mục`}
                    highlighted
                  />
                </div>
              </div>

              {/* Body 2 columns */}
              <div
                className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8"
                data-tour="lesson-create-form"
              >
                {bootLoading ? (
                  <div className="flex items-center justify-center gap-2 py-20 text-slate-500">
                    <Loader2 className="animate-spin" size={22} />
                    <span className="text-sm font-medium">
                      Đang tải bài học…
                    </span>
                  </div>
                ) : (
                <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.55fr)_340px]">
                  {/* LEFT */}
                  <div className="space-y-6">
                    <SectionCard
                      icon={Layers}
                      title="Liên kết khóa học"
                      description="Chọn môn học trước, sau đó chọn khóa học thuộc môn đó."
                      tourId="lesson-create-linking"
                    >
                      <div className="space-y-5">
                        <CustomSelect
                          label="Môn học"
                          required
                          placeholder="Chọn môn học"
                          loading={subjectsLoading}
                          disabled={loading}
                          value={subjectId}
                          options={subjects.map((s) => ({
                            label: s.name,
                            value: s.id,
                          }))}
                          onChange={(val) => {
                            setSubjectId(val || "");
                            setCourseId("");
                            setSelectedDocIds([]);
                            setDocuments([]);
                          }}
                        />
                        <CustomSelect
                          label="Khóa học"
                          required
                          placeholder={
                            subjectId
                              ? "Chọn khóa học"
                              : "Chọn môn học trước"
                          }
                          loading={coursesLoading}
                          disabled={loading || !subjectId}
                          value={courseId}
                          options={courses.map((c) => ({
                            label: c.name,
                            value: c.id,
                          }))}
                          onChange={(val) => {
                            setCourseId(val || "");
                            setSelectedDocIds([]);
                          }}
                        />
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={FileText}
                      title="Nội dung bài học"
                      description="Thứ tự hiển thị, tên bài và mô tả tóm tắt."
                      tourId="lesson-create-content"
                    >
                      <div
                        className={`space-y-5 ${
                          courseId ? "" : "pointer-events-none opacity-60"
                        }`}
                      >
                        {!courseId ? (
                          <div className="flex items-start gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <Info
                              size={16}
                              className="mt-0.5 shrink-0 text-amber-600"
                            />
                            Chọn khóa học ở nhóm trên trước khi nhập nội dung.
                          </div>
                        ) : null}
                        <div>
                          <FieldLabel required={isEdit}>Số thứ tự</FieldLabel>
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            value={lessonOrder}
                            onChange={(e) =>
                              setLessonOrder(
                                e.target.value.replace(/[^\d]/g, "")
                              )
                            }
                            disabled={loading || !courseId}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-50 disabled:bg-slate-50"
                            placeholder={
                              isEdit
                                ? "Thứ tự hiển thị"
                                : "Để trống = xếp sau bài cuối"
                            }
                          />
                        </div>
                        {isEdit ? (
                          <div>
                            <FieldLabel>Trạng thái</FieldLabel>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: true, label: "Đang hoạt động" },
                                { value: false, label: "Chờ cập nhật" },
                              ].map((opt) => {
                                const active = isActive === opt.value;
                                return (
                                  <button
                                    key={String(opt.value)}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => setIsActive(opt.value)}
                                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                                      active
                                        ? "border-red-600 bg-red-600 text-white"
                                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                    } disabled:opacity-50`}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                        <div>
                          <FieldLabel required>Tên bài học</FieldLabel>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) =>
                              setTitle(toTitleCaseVietnamese(e.target.value))
                            }
                            disabled={loading || !courseId}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-50 disabled:bg-slate-50"
                            placeholder="VD: Bài 01: Giới Thiệu Về Phép Tính"
                          />
                        </div>
                        <div>
                          <FieldLabel>Mô tả</FieldLabel>
                          <textarea
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={loading || !courseId}
                            className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-50 disabled:bg-slate-50"
                            placeholder="Ghi chú ngắn (không bắt buộc)"
                          />
                        </div>

                        <div>
                          <FieldLabel hint="Tuỳ chọn">Ảnh thumbnail</FieldLabel>
                          <div
                            className={`relative overflow-hidden rounded-3xl border-2 border-dashed px-5 py-5 transition ${
                              thumbPreview
                                ? "border-red-200 bg-red-50/40"
                                : "border-slate-200 bg-slate-50/70 hover:border-red-300 hover:bg-red-50/20"
                            } ${loading || thumbBusy || !courseId ? "opacity-60" : ""}`}
                          >
                            <input
                              type="file"
                              accept={LESSON_THUMB.accept}
                              className="absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                              onChange={handleThumbPick}
                              disabled={loading || thumbBusy || !courseId}
                            />
                            <div className="flex items-start gap-4">
                              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">
                                {thumbPreview ? (
                                  <img
                                    src={thumbPreview}
                                    alt="Thumbnail"
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
                                    <ImagePlus size={22} />
                                    <span className="text-[9px] font-bold uppercase tracking-wide">
                                      1:1
                                    </span>
                                  </div>
                                )}
                                {thumbBusy ? (
                                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                                    <Loader2
                                      size={18}
                                      className="animate-spin text-red-600"
                                    />
                                  </div>
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-800">
                                  {thumbPreview
                                    ? `Đã cắt ${LESSON_THUMB.width}×${LESSON_THUMB.height} · ${formatThumbBytes(thumbBytes)}`
                                    : "Chọn hoặc kéo thả ảnh"}
                                </p>
                                <p className="mt-1 text-xs leading-relaxed text-slate-400">
                                  {LESSON_THUMB.note}
                                </p>
                                {thumbPreview ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      clearThumb();
                                    }}
                                    className="relative z-20 mt-2 rounded-xl px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-white hover:text-red-600"
                                  >
                                    Gỡ ảnh
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={Library}
                      title="Tài liệu liên quan"
                      description="Tài liệu có thể gắn theo khóa học hoặc dùng chung cả môn (khi tạo tài liệu để trống khóa học)."
                      tourId="lesson-create-documents"
                    >
                      <div className="space-y-4">
                        <div>
                          <FieldLabel>Phạm vi hiển thị</FieldLabel>
                          <div className="flex flex-wrap gap-2">
                            {[
                              {
                                value: "course_or_shared",
                                label: "Khóa + dùng chung môn",
                              },
                              { value: "subject", label: "Theo môn học" },
                              { value: "course", label: "Theo khóa học" },
                            ].map((opt) => {
                              const active = docScope === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  disabled={loading}
                                  onClick={() => {
                                    setDocScope(opt.value);
                                    setSelectedDocIds([]);
                                  }}
                                  className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                                    active
                                      ? "border-red-600 bg-red-600 text-white"
                                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                  } disabled:opacity-50`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-2 text-xs leading-relaxed text-slate-400">
                            {docScope === "course"
                              ? "Chỉ tài liệu đã gắn đúng khóa học đang chọn."
                              : docScope === "subject"
                                ? "Mọi tài liệu thuộc môn học (kể cả gắn khóa khác hoặc dùng chung môn)."
                                : "Tài liệu của khóa đang chọn + tài liệu dùng chung môn (không gắn khóa)."}
                          </p>
                        </div>

                        {docsScopeHint ? (
                          <div className="flex items-start gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <Info
                              size={16}
                              className="mt-0.5 shrink-0 text-amber-600"
                            />
                            {docsScopeHint}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <CustomMultiSearchSelect
                              label="Chọn tài liệu"
                              required
                              hint={`${selectedDocIds.length} đã chọn`}
                              values={selectedDocIds}
                              onChange={setSelectedDocIds}
                              options={documentOptions}
                              loading={docsLoading}
                              disabled={loading || !canPickDocuments}
                              placeholder="Tìm và chọn tài liệu…"
                              emptyMessage={
                                docScope === "course"
                                  ? "Không có tài liệu gắn đúng khóa học này"
                                  : "Không có tài liệu trong phạm vi đã chọn"
                              }
                            />

                            {selectedDocs.length > 0 ? (
                              <ul className="space-y-2">
                                {selectedDocs.map((doc, idx) => (
                                  <li
                                    key={doc.id}
                                    className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-3 py-2.5"
                                  >
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-50 text-xs font-black text-red-700">
                                      {idx + 1}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-sm font-medium text-slate-700">
                                        {doc.file_name}
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        {doc.course_name || "Dùng chung môn"}
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      title="Bỏ tài liệu"
                                      onClick={() => toggleDoc(doc.id)}
                                      disabled={loading}
                                      className="rounded-xl p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </SectionCard>
                  </div>

                  {/* RIGHT — kết quả khởi tạo */}
                  <div
                    className="space-y-5 xl:sticky xl:top-0"
                    data-tour="lesson-create-preview"
                  >
                    <div className="rounded-[1.75rem] border border-slate-200 bg-slate-900 p-5 text-white shadow-xl shadow-slate-900/10">
                      <div className="mb-4 flex items-center gap-3">
                        <div className="flex h-11 w-11 overflow-hidden rounded-2xl bg-white/10">
                          {thumbPreview ? (
                            <img
                              src={thumbPreview}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <BookOpen size={20} />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                            Kết quả khởi tạo
                          </p>
                          <p className="truncate text-base font-bold">
                            {title.trim() || "Chưa có tên bài học"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <SummaryRow
                          label="Môn học"
                          value={subjectLabel || "Chưa chọn"}
                          dark
                          muted={!subjectLabel}
                        />
                        <SummaryRow
                          label="Khóa học"
                          value={
                            subjectId
                              ? courseLabel || "Chưa chọn khóa"
                              : "Chưa chọn môn"
                          }
                          dark
                          muted={!courseLabel}
                        />
                        <SummaryRow
                          label="Thứ tự"
                          value={
                            String(lessonOrder).trim() !== ""
                              ? lessonOrder
                              : "Tự động"
                          }
                          dark
                          muted={String(lessonOrder).trim() === ""}
                        />
                        <SummaryRow
                          label="Tài liệu"
                          value={
                            selectedDocIds.length > 0
                              ? `${selectedDocIds.length} tệp`
                              : "Chưa chọn"
                          }
                          dark
                          muted={selectedDocIds.length === 0}
                        />
                        <SummaryRow
                          label="Thumbnail"
                          value={
                            thumbPreview
                              ? formatThumbBytes(thumbBytes) || "Đã chọn"
                              : "Chưa có"
                          }
                          dark
                          muted={!thumbPreview}
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="mb-4 flex items-center gap-2">
                        <Library size={18} className="text-red-500" />
                        <h3 className="font-bold text-slate-800">
                          Tóm tắt bài học
                        </h3>
                      </div>
                      <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-4">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-red-400">
                          Tài liệu gắn kèm
                        </p>
                        <p className="mt-1 text-2xl font-black text-red-700">
                          {selectedDocIds.length > 0
                            ? `${selectedDocIds.length} tài liệu`
                            : "Chưa chọn"}
                        </p>
                        <p className="mt-2 line-clamp-2 text-xs italic text-red-500">
                          {selectedDocs.length > 0
                            ? selectedDocs.map((d) => d.file_name).join(", ")
                            : "Chọn tài liệu ở cột trái để xem kết quả tại đây"}
                        </p>
                      </div>
                      <div className="space-y-3">
                        <SummaryRow
                          label="Hoàn thiện"
                          value={`${completionCount}/4 mục chính`}
                          muted={completionCount < 4}
                        />
                        <SummaryRow
                          label="Mô tả"
                          value={
                            description?.trim()
                              ? description.trim()
                              : "Không có"
                          }
                          muted={!description?.trim()}
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-amber-100 bg-amber-50 p-5">
                      <div className="flex items-start gap-3">
                        <Info
                          size={18}
                          className="mt-0.5 shrink-0 text-amber-600"
                        />
                        <div>
                          <p className="text-sm font-bold text-amber-800">
                            Lưu ý trước khi lưu
                          </p>
                          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-amber-700">
                            <li>Môn học → khóa học → tên bài → tài liệu là bắt buộc.</li>
                            <li>
                              Có thể gắn nhiều tài liệu cho một bài học.
                            </li>
                            <li>
                              Thumbnail khuyến nghị 320×320 (1:1); ảnh khác kích
                              thước sẽ được cắt giữa khung và nén (~40KB) rồi lưu
                              trực tiếp trong DB (BYTEA) — không dùng GCS.
                            </li>
                            <li>
                              Để trống số thứ tự nếu muốn hệ thống xếp sau bài
                              cuối.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-6 py-5 md:px-8"
                data-tour="lesson-create-actions"
              >
                <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading || bootLoading}
                    className="rounded-2xl px-6 py-3 text-sm font-bold text-slate-500 transition hover:text-red-600 disabled:opacity-50"
                  >
                    Hủy bỏ
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={loading || bootLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700 active:scale-[0.98] disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <CheckCircle2 size={18} />
                    )}
                    {loading
                      ? "Đang lưu…"
                      : isEdit
                        ? "Lưu thay đổi"
                        : "Lưu bài học"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LessonCreateModal;
